/**
 * Hierarchical category taxonomy.
 *
 * Category *keys* are stable machine identifiers and are what gets stored in the
 * database. Display names are looked up per-locale so a user switching to Arabic
 * never sees an English category, and so renaming a label never migrates data.
 */
const sub = (key, en, ar) => ({ key, en, ar });
export const CATEGORIES = [
    {
        key: 'housing', en: 'Housing', ar: 'السكن', icon: '🏠', color: '#295dd6',
        subcategories: [
            sub('rent', 'Rent', 'الإيجار'),
            sub('mortgage', 'Mortgage', 'الرهن العقاري'),
            sub('maintenance', 'Maintenance', 'الصيانة'),
            sub('furniture', 'Furniture', 'الأثاث'),
            sub('service_charges', 'Service charges', 'رسوم الخدمات'),
        ],
    },
    {
        key: 'groceries', en: 'Groceries', ar: 'البقالة', icon: '🛒', color: '#29d668',
        subcategories: [
            sub('supermarket', 'Supermarket', 'سوبرماركت'),
            sub('bakery', 'Bakery', 'مخبز'),
            sub('butcher', 'Butcher', 'ملحمة'),
            sub('convenience', 'Convenience store', 'بقالة صغيرة'),
        ],
    },
    {
        key: 'restaurants', en: 'Restaurants', ar: 'المطاعم', icon: '🍽️', color: '#d69729',
        subcategories: [
            sub('dine_in', 'Dine in', 'تناول في المطعم'),
            sub('delivery', 'Delivery', 'توصيل'),
            sub('fast_food', 'Fast food', 'وجبات سريعة'),
            sub('dessert', 'Dessert', 'حلويات'),
        ],
    },
    {
        key: 'coffee', en: 'Coffee', ar: 'القهوة', icon: '☕', color: '#845433',
        subcategories: [sub('cafe', 'Café', 'مقهى'), sub('specialty', 'Specialty coffee', 'قهوة مختصة')],
    },
    {
        key: 'transportation', en: 'Transportation', ar: 'المواصلات', icon: '🚕', color: '#29c5d6',
        subcategories: [
            sub('taxi', 'Taxi & ride hailing', 'تاكسي وتطبيقات النقل'),
            sub('metro', 'Metro & bus', 'مترو وحافلات'),
            sub('parking', 'Parking', 'مواقف'),
            sub('tolls', 'Tolls (Salik)', 'رسوم المرور (سالك)'),
            sub('car_rental', 'Car rental', 'تأجير سيارات'),
            // Washing, servicing, tyres, registration. Distinct from Fuel, which is
            // what the car consumes rather than what it costs to keep.
            sub('car_care', 'Car care & wash', 'العناية بالسيارة والغسيل'),
        ],
    },
    {
        key: 'fuel', en: 'Fuel', ar: 'الوقود', icon: '⛽', color: '#d63429',
        subcategories: [sub('petrol', 'Petrol', 'بنزين'), sub('ev_charging', 'EV charging', 'شحن كهربائي')],
    },
    {
        key: 'shopping', en: 'Shopping', ar: 'التسوق', icon: '🛍️', color: '#d629a2',
        subcategories: [
            sub('online', 'Online', 'تسوق إلكتروني'),
            // Tabby, Tamara and the rest: the payment, not the thing bought. The app
            // cannot see what the plan was for — the message names only the provider.
            sub('installments', 'Pay in instalments', 'الدفع بالتقسيط'),
            sub('home', 'Home goods', 'مستلزمات المنزل'),
            sub('books', 'Books', 'كتب'),
            sub('toys', 'Toys', 'ألعاب'),
        ],
    },
    {
        key: 'clothing', en: 'Clothing', ar: 'الملابس', icon: '👕', color: '#848133',
        subcategories: [
            sub('apparel', 'Apparel', 'ملابس'),
            sub('shoes', 'Shoes', 'أحذية'),
            sub('accessories', 'Accessories', 'إكسسوارات'),
        ],
    },
    {
        key: 'electronics', en: 'Electronics', ar: 'الإلكترونيات', icon: '💻', color: '#9584eb',
        subcategories: [
            sub('devices', 'Devices', 'أجهزة'),
            sub('accessories', 'Accessories', 'ملحقات'),
            sub('software', 'Software', 'برمجيات'),
        ],
    },
    {
        key: 'entertainment', en: 'Entertainment', ar: 'الترفيه', icon: '🎬', color: '#7a29d6',
        subcategories: [
            sub('cinema', 'Cinema', 'سينما'),
            sub('events', 'Events', 'فعاليات'),
            sub('gaming', 'Gaming', 'ألعاب إلكترونية'),
            sub('attractions', 'Attractions', 'أماكن ترفيهية'),
        ],
    },
    {
        key: 'travel', en: 'Travel', ar: 'السفر', icon: '✈️', color: '#336484',
        subcategories: [
            sub('flights', 'Flights', 'رحلات جوية'),
            sub('hotels', 'Hotels', 'فنادق'),
            sub('visa', 'Visa', 'تأشيرات'),
            sub('transport', 'Local transport', 'مواصلات محلية'),
        ],
    },
    {
        key: 'health', en: 'Health', ar: 'الصحة', icon: '🏥', color: '#33846c',
        subcategories: [
            sub('pharmacy', 'Pharmacy', 'صيدلية'),
            sub('clinic', 'Clinic & hospital', 'عيادة ومستشفى'),
            sub('dental', 'Dental', 'أسنان'),
            sub('optical', 'Optical', 'بصريات'),
        ],
    },
    {
        key: 'education', en: 'Education', ar: 'التعليم', icon: '🎓', color: '#363384',
        subcategories: [
            sub('tuition', 'Tuition', 'رسوم دراسية'),
            sub('courses', 'Courses', 'دورات'),
            sub('supplies', 'Supplies', 'مستلزمات دراسية'),
        ],
    },
    {
        key: 'bills_utilities', en: 'Bills & Utilities', ar: 'الفواتير والخدمات', icon: '💡', color: '#b4d629',
        subcategories: [
            sub('electricity_water', 'Electricity & water', 'كهرباء وماء'),
            sub('mobile', 'Mobile', 'الهاتف'),
            sub('internet', 'Internet', 'الإنترنت'),
            sub('cooling', 'District cooling', 'التبريد المركزي'),
        ],
    },
    {
        key: 'subscriptions', en: 'Subscriptions', ar: 'الاشتراكات', icon: '🔁', color: '#793384',
        subcategories: [
            sub('streaming', 'Streaming', 'بث'),
            sub('music', 'Music', 'موسيقى'),
            sub('cloud', 'Cloud & software', 'سحابة وبرمجيات'),
            sub('news', 'News', 'أخبار'),
        ],
    },
    {
        key: 'insurance', en: 'Insurance', ar: 'التأمين', icon: '🛡️', color: '#adeb84',
        subcategories: [
            sub('health', 'Health insurance', 'تأمين صحي'),
            sub('car', 'Car insurance', 'تأمين سيارة'),
            sub('home', 'Home insurance', 'تأمين منزل'),
            sub('life', 'Life insurance', 'تأمين حياة'),
        ],
    },
    {
        key: 'government', en: 'Government', ar: 'الحكومة', icon: '🏛️', color: '#84b8eb',
        subcategories: [
            sub('fines', 'Fines', 'مخالفات'),
            sub('fees', 'Government fees', 'رسوم حكومية'),
            sub('visa', 'Visa & immigration', 'تأشيرات وإقامة'),
        ],
    },
    {
        key: 'family', en: 'Family', ar: 'العائلة', icon: '👨‍👩‍👧', color: '#4c8433',
        subcategories: [
            sub('childcare', 'Childcare', 'رعاية الأطفال'),
            sub('school', 'School', 'المدرسة'),
            sub('support', 'Family support', 'مصروف العائلة'),
            sub('domestic_help', 'Domestic help', 'العمالة المنزلية'),
        ],
    },
    {
        key: 'gifts', en: 'Gifts', ar: 'الهدايا', icon: '🎁', color: '#eb84b1',
        subcategories: [sub('gifts', 'Gifts', 'هدايا'), sub('charity', 'Charity & zakat', 'صدقة وزكاة')],
    },
    {
        key: 'personal_care', en: 'Personal Care', ar: 'العناية الشخصية', icon: '💇', color: '#e484eb',
        subcategories: [
            sub('salon', 'Salon & barber', 'صالون وحلاقة'),
            sub('cosmetics', 'Cosmetics', 'مستحضرات تجميل'),
            sub('spa', 'Spa', 'سبا'),
        ],
    },
    {
        key: 'fitness', en: 'Fitness', ar: 'اللياقة', icon: '🏋️', color: '#84ebc5',
        subcategories: [
            sub('gym', 'Gym', 'نادي رياضي'),
            sub('classes', 'Classes', 'حصص رياضية'),
            sub('equipment', 'Equipment', 'معدات'),
        ],
    },
    {
        key: 'business', en: 'Business', ar: 'الأعمال', icon: '💼', color: '#eb9c84',
        subcategories: [
            sub('software', 'Software', 'برمجيات'),
            sub('supplies', 'Supplies', 'مستلزمات'),
            sub('travel', 'Business travel', 'سفر عمل'),
            sub('services', 'Professional services', 'خدمات مهنية'),
        ],
    },
    {
        key: 'transfers', en: 'Transfers', ar: 'التحويلات', icon: '🔄', color: '#94a3b8', nonSpending: true,
        subcategories: [
            sub('own_accounts', 'Between my accounts', 'بين حساباتي'),
            sub('remittance', 'Remittance', 'حوالة'),
            sub('person', 'To a person', 'إلى شخص'),
        ],
    },
    {
        key: 'atm_cash', en: 'ATM / Cash', ar: 'الصراف / النقد', icon: '🏧', color: '#ebd684',
        subcategories: [sub('withdrawal', 'Withdrawal', 'سحب نقدي'), sub('deposit', 'Deposit', 'إيداع')],
    },
    {
        key: 'fees', en: 'Fees', ar: 'الرسوم', icon: '🧾', color: '#84334e',
        subcategories: [
            sub('bank', 'Bank fees', 'رسوم بنكية'),
            sub('fx', 'Foreign exchange', 'رسوم عملة أجنبية'),
            sub('interest', 'Interest', 'فوائد'),
            sub('late', 'Late payment', 'تأخر السداد'),
        ],
    },
    {
        /**
         * Money coming back, kept apart from money coming in.
         *
         * Refunds used to be filed under Income, which made every screen that names
         * a category call a returned purchase "Income" — while every screen that
         * sums money treated it as a refund and left it out of the income total.
         * The app contradicted itself about the same transaction, and the label was
         * the half that was wrong: a refund is not earnings, it is a purchase
         * undone.
         */
        key: 'refunds', en: 'Refund', ar: 'استرداد', icon: '↩️', color: '#84eb95', nonSpending: true,
        subcategories: [
            sub('purchase', 'Returned purchase', 'شراء مُعاد'),
            sub('cancellation', 'Cancellation', 'إلغاء'),
            sub('reversal', 'Reversal', 'عملية عكسية'),
            sub('cashback', 'Cashback', 'استرداد نقدي'),
        ],
    },
    {
        key: 'income', en: 'Income', ar: 'الدخل', icon: '💰', color: '#157f3c', nonSpending: true,
        subcategories: [
            sub('salary', 'Salary', 'الراتب'),
            sub('bonus', 'Bonus', 'مكافأة'),
            sub('freelance', 'Freelance', 'عمل حر'),
            sub('refund', 'Refund', 'استرداد'),
            sub('interest', 'Interest & dividends', 'فوائد وأرباح'),
            sub('other', 'Other income', 'دخل آخر'),
        ],
    },
    {
        key: 'other', en: 'Other', ar: 'أخرى', icon: '❓', color: '#a8a29e',
        subcategories: [sub('uncategorized', 'Uncategorized', 'غير مصنف')],
    },
];
export const CATEGORY_KEYS = CATEGORIES.map((c) => c.key);
const CATEGORY_INDEX = new Map(CATEGORIES.map((c) => [c.key, c]));
export function getCategory(key) {
    return CATEGORY_INDEX.get(key);
}
export function isValidCategory(key) {
    return CATEGORY_INDEX.has(key);
}
export function isValidSubcategory(categoryKey, subKey) {
    if (subKey == null)
        return true;
    const cat = CATEGORY_INDEX.get(categoryKey);
    if (!cat)
        return false;
    return cat.subcategories.some((s) => s.key === subKey);
}
export function categoryLabel(key, locale = 'en') {
    const cat = CATEGORY_INDEX.get(key);
    if (!cat)
        return key;
    return locale === 'ar' ? cat.ar : cat.en;
}
export function subcategoryLabel(categoryKey, subKey, locale = 'en') {
    if (!subKey)
        return null;
    const cat = CATEGORY_INDEX.get(categoryKey);
    const s = cat?.subcategories.find((x) => x.key === subKey);
    if (!s)
        return subKey;
    return locale === 'ar' ? s.ar : s.en;
}
export function categoryColor(key) {
    return CATEGORY_INDEX.get(key)?.color ?? '#a8a29e';
}
export function categoryIcon(key) {
    return CATEGORY_INDEX.get(key)?.icon ?? '❓';
}
/** Categories that count toward "how much did I spend" figures. */
export function isSpendingCategory(key) {
    return !CATEGORY_INDEX.get(key)?.nonSpending;
}
export const DEFAULT_CATEGORY = 'other';
export const DEFAULT_SUBCATEGORY = 'uncategorized';
//# sourceMappingURL=categories.js.map